import { API_ENDPOINTS } from "../../enums/apiUrls";
import { getBearerToken } from "./bearerToken";

/**
 * Request props for {@link prepareFormQuestionnaire}.
 *
 * Mirrors the backend `PrepareFormQuestionnaireRequest` model. Tenant scope
 * comes from the bearer token (`get_current_facility_id_from_request`); the
 * body does not accept `org_id`.
 */
interface PrepareFormQuestionnaireProps {
  formId: string;
  orderId: string;
}

type FastApiValidationItem = {
  msg?: string;
  type?: string;
  loc?: (string | number)[];
};

/**
 * Extracts a human-readable message from a FastAPI error response body.
 *
 * @param status - HTTP status code from the failed response
 * @param body - Parsed JSON body, or null when parsing failed
 * @returns Validation `msg`, string `detail`, or a generic status fallback
 */
function messageFromErrorBody(status: number, body: unknown): string {
  if (body && typeof body === "object" && "detail" in body) {
    const detail = (body as { detail: unknown }).detail;
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0] as FastApiValidationItem;
      if (first?.msg) {
        return first.msg;
      }
    }
    if (typeof detail === "string") {
      return detail;
    }
  }
  return `Request failed (${status})`;
}

export type PrepareFormQuestionnaireResult =
  | { ok: true; data: unknown }
  | { ok: false; status: number; message: string };

/**
 * Seeds the fax questionnaire for an order (AI draft + Firestore/PG dual-write).
 *
 * @param props - Form and order ids
 * @returns Success with parsed JSON, or a structured failure (no throw on HTTP errors)
 */
export const prepareFormQuestionnaire = async (
  props: PrepareFormQuestionnaireProps,
): Promise<PrepareFormQuestionnaireResult> => {
  const token = await getBearerToken();

  const response = await fetch(API_ENDPOINTS.PREPARE_FORM_QUESTIONNAIRE, {
    method: "POST",
    body: JSON.stringify({
      form_id: props.formId,
      order_id: props.orderId,
    }),
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    return {
      ok: false,
      status: response.status,
      message: messageFromErrorBody(response.status, errorData),
    };
  }

  const data = await response.json();
  return { ok: true, data };
};
